import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Button, Avatar, Dropdown, Modal } from 'antd';
import GlobalDrawer from '../GlobalDrawer/GlobalDrawer';
import { useAuth } from '@/contexts/AuthContext';
import { usePermission } from '@/contexts/PermissionContext';
import { routeConfig, buildPathToKey, buildKeyToPath } from '@/config/routes';
import {
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';

const { Header, Sider, Content } = Layout;

interface MainLayoutProps {
  children: React.ReactNode;
}

// 从统一配置构建映射表
const ROUTE_MAP = buildKeyToPath();
const PATH_TO_KEY = buildPathToKey();

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isInitialMount = useRef(true);
  const { userInfo, logout } = useAuth();
  const { canShowMenu } = usePermission();

  // 根据当前路由获取选中的菜单项
  const selectedKeys = useMemo(() => {
    const key = PATH_TO_KEY[location.pathname];
    return key ? [key] : ['1'];
  }, [location.pathname]);

  // 根据当前路由获取打开的子菜单
  const openKeys = useMemo(() => {
    const key = PATH_TO_KEY[location.pathname];
    if (['3', '4', '5'].includes(key)) return ['ota'];
    if (['6', '7', '8'].includes(key)) return ['statistics'];
    if (['9', '10', '11'].includes(key)) return ['system'];
    return [];
  }, [location.pathname]);

  // 监听路由变化，自动关闭抽屉
  useEffect(() => {
    // 跳过初始渲染，只在路由真正变化时关闭抽屉
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
  }, [location.pathname]);

  // 处理菜单点击
  const handleMenuClick = ({ key }: { key: string }) => {
    const path = ROUTE_MAP[key];
    if (path) {
      navigate(path);
    }
  };

  // 处理用户菜单点击
  const handleUserMenuClick = ({ key }: { key: string }) => {
    if (key === 'profile') {
      navigate('/account');
    } else if (key === 'logout') {
      handleLogout();
    }
  };

  // 处理退出登录
  const handleLogout = () => {
    Modal.confirm({
      title: '确认退出',
      content: '您确定要退出登录吗？',
      okText: '确认',
      cancelText: '取消',
      onOk() {
        logout();
      },
    });
  };

  // 根据权限过滤菜单项（使用统一配置）
  const menuItems: MenuProps['items'] = useMemo(() => {
    const buildMenuItems = (routes: typeof routeConfig): MenuProps['items'] => {
      return routes
        .map(route => {
          // 如果有子菜单，递归处理
          if (route.children) {
            const children = buildMenuItems(route.children);
            // 只有当有可见的子菜单时才显示父菜单
            if (children && children.length > 0) {
              return {
                key: route.key,
                icon: route.icon ? <route.icon /> : undefined,
                label: route.label,
                children,
              };
            }
            return null;
          }
          
          // 检查权限
          if (!canShowMenu(route.key)) {
            return null;
          }
          
          return {
            key: route.key,
            icon: route.icon ? <route.icon /> : undefined,
            label: route.label,
          };
        })
        .filter(Boolean) as MenuProps['items'];
    };

    return buildMenuItems(routeConfig);
  }, [canShowMenu]);

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '账号管理',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  return (
    <Layout className="min-h-screen">
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed}
        className="bg-bg-secondary border-r border-slate-700"
        width={240}
      >
        <div className="h-16 flex items-center justify-center border-b border-slate-700">
          <h1 className="text-xl font-bold">
            {collapsed ? 'OTT' : 'OTT OTA管理平台'}
          </h1>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={selectedKeys}
          defaultOpenKeys={openKeys}
          items={menuItems}
          className="bg-bg-secondary border-r-0"
          style={{ background: '#1a1f2e' }}
          onClick={handleMenuClick}
        />
      </Sider>
      
      <Layout>
        <Header className="bg-bg-primary px-4 flex items-center justify-between border-b border-slate-700">
          <div className="flex items-center">
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              className="text-white hover:bg-slate-700"
            />
          </div>
          
          <div className="flex items-center space-x-4">
            <Dropdown 
              menu={{ 
                items: userMenuItems,
                onClick: handleUserMenuClick
              }} 
              placement="bottomRight"
            >
              <div className="flex items-center space-x-2 cursor-pointer  px-3 py-2 rounded">
                <Avatar size="small" icon={<UserOutlined />} />
                <span >{userInfo?.email || 'admin'}</span>
              </div>
            </Dropdown>
          </div>
        </Header>
        
        <Content className="p-6 bg-bg-primary min-h-screen">
          {children}
        </Content>
      </Layout>
      
      {/* 全局Drawer */}
      <GlobalDrawer />
    </Layout>
  );
};

export default MainLayout;
